import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getAlerts } from './request/request';
import { AlertsData, AlertItem } from './Srv6Types';
import InlineSpinner from './InlineSpinner';
import ErrorBanner from './ErrorBanner';
import SeverityBadge from './SeverityBadge';
import Tooltip from '../Topology/components/Tooltip';
import FreshnessBar from './FreshnessBar';

interface Props {
    timeRange: string;
    onNavigate?: (tab: string, source?: string) => void;
}

const ALERT_CARDS = [
    { key: 'interface_capacity', label: 'Interface Capacity', severity: 'high', tooltip: 'A router exceeded the configured SRv6 throughput threshold in the current window.' },
    { key: 'suspicious_silence', label: 'Suspicious Silence', severity: 'high', tooltip: 'A flow that was actively carrying traffic dropped to zero unexpectedly.' },
    { key: 'silent_locator', label: 'Silent Locators', severity: 'medium', tooltip: 'A flex-algo has zero traffic across multiple routers over several days.' },
    { key: 'flow_drift', label: 'Flow Drift', severity: 'medium', tooltip: 'A flow changed significantly (>50%) compared to 1 hour ago.' },
] as const;

const ALERT_TYPE_LABELS: Record<string, string> = {
    interface_capacity: 'Capacity',
    silent_locator: 'Silent Locator',
    flow_drift: 'Flow Drift',
    suspicious_silence: 'Silence',
};

function extractSource(link: string): string | undefined {
    try {
        const url = new URL(link, 'http://localhost');
        return url.searchParams.get('source') || undefined;
    } catch {
        return undefined;
    }
}

const AlertRow: React.FC<{
    alert: AlertItem;
    onNavigate?: (tab: string, source?: string) => void;
}> = ({ alert, onNavigate }) => {
    const source = extractSource(alert.detailLink);
    const hasLink = !!source && !!onNavigate;

    const content = (
        <>
            <SeverityBadge severity={alert.severity} className="min-w-[50px] text-center" />
            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-semantic-bg-secondary text-semantic-text-secondary whitespace-nowrap">
                {ALERT_TYPE_LABELS[alert.type] || alert.type}
            </span>
            <span className="text-xs font-mono text-semantic-text-primary min-w-[120px]">
                {alert.resource}
            </span>
            <span className="text-xs text-semantic-text-secondary flex-1 text-left">{alert.message}</span>
            {hasLink && (
                <svg className="w-3.5 h-3.5 text-semantic-text-secondary/50 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 4l4 4-4 4" />
                </svg>
            )}
        </>
    );

    const cls = `flex items-center gap-3 p-3 border rounded-xl w-full
        border-semantic-border-secondary bg-semantic-bg-primary transition-colors
        ${hasLink ? 'hover:bg-semantic-bg-secondary/40 cursor-pointer' : ''}`;

    if (hasLink) {
        return (
            <button type="button" className={cls} onClick={() => onNavigate('traffic', source)}>
                {content}
            </button>
        );
    }
    return <div className={cls}>{content}</div>;
};

const Srv6AlertsTab: React.FC<Props> = ({ timeRange, onNavigate }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [alertsData, setAlertsData] = useState<AlertsData | null>(null);
    const [lastFetched, setLastFetched] = useState<Date | null>(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = await getAccessTokenSilently();
            const res = await getAlerts(token, { timeRange });
            if (res.data?.data) {
                setAlertsData(res.data.data);
                setLastFetched(new Date());
            }
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to load alerts');
        } finally {
            setIsLoading(false);
        }
    }, [getAccessTokenSilently, timeRange]);

    useEffect(() => { load(); }, [load]);

    if (isLoading) return <InlineSpinner />;
    if (error) return <ErrorBanner message={error} onRetry={load} />;

    return (
        <div className="flex flex-col gap-4 overflow-auto">
            <div className="flex items-start justify-between gap-4">
                <p className="text-[11px] text-semantic-text-secondary leading-relaxed">
                    Real-time conditions that deviate from expected behavior. Each card counts active alerts of that type in the current time window.
                </p>
                <FreshnessBar lastFetched={lastFetched} onRefresh={load} />
            </div>
            {alertsData && (
                <div className="grid grid-cols-4 gap-3">
                    {ALERT_CARDS.map((card) => (
                        <Tooltip key={card.key} text={card.tooltip}>
                            <div className="p-4 rounded-xl border border-semantic-border-secondary bg-semantic-bg-primary w-full">
                                <div className="text-xs text-semantic-text-secondary">{card.label}</div>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className="text-2xl font-bold text-semantic-text-primary">
                                        {alertsData.summary[card.key] || 0}
                                    </span>
                                    <SeverityBadge severity={card.severity} />
                                </div>
                            </div>
                        </Tooltip>
                    ))}
                </div>
            )}

            <div className="flex flex-col gap-1">
                {alertsData?.alerts.map((a, i) => (
                    <AlertRow
                        key={`${a.type}-${a.resource}-${i}`}
                        alert={a}
                        onNavigate={onNavigate}
                    />
                ))}
                {(!alertsData || alertsData.alerts.length === 0) && (
                    <div className="flex items-center justify-center py-8 text-sm text-semantic-text-secondary">
                        No active alerts
                    </div>
                )}
            </div>
        </div>
    );
};

export default Srv6AlertsTab;
